import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { AlertTriangle, CheckCircle2, Clock, ExternalLink, RefreshCw } from "lucide-react"

import { Button } from "@aix/ui"
import { trpc } from "~/lib/trpc"

type StatusFilter = "open" | "investigating" | "resolved" | "any"
type SourceFilter = "ping" | "scenario" | "sentry" | "manual" | "deploy_smoke" | "vendor" | "any"

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: "open", label: "Open + investigating" },
  { value: "resolved", label: "Resolved" },
  { value: "any", label: "Any status" },
]

const SOURCE_OPTIONS: Array<{ value: SourceFilter; label: string }> = [
  { value: "any", label: "Any source" },
  { value: "ping", label: "Ping" },
  { value: "scenario", label: "Scenario" },
  { value: "sentry", label: "Sentry" },
  { value: "manual", label: "Manual" },
  { value: "deploy_smoke", label: "Deploy smoke" },
  { value: "vendor", label: "Vendor" },
]

function formatRelativeTime(iso: string | Date): string {
  const date = typeof iso === "string" ? new Date(iso) : iso
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "—"
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const remM = minutes % 60
  return remM > 0 ? `${hours}h ${remM}m` : `${hours}h`
}

function StatusBadge({ status, resolvedAt }: { status: string; resolvedAt: string | Date | null }) {
  if (resolvedAt) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
        <CheckCircle2 className="h-3 w-3" />
        resolved
      </span>
    )
  }
  const styles: Record<string, string> = {
    open: "bg-red-100 text-red-700",
    investigating: "bg-amber-100 text-amber-700",
    mitigated: "bg-blue-100 text-blue-700",
  }
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${styles[status] ?? "bg-gray-100 text-gray-700"}`}
    >
      <AlertTriangle className="h-3 w-3" />
      {status}
    </span>
  )
}

function SourceBadge({ source }: { source: string }) {
  return (
    <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-gray-600">
      {source}
    </span>
  )
}

export default function IncidentsScreen() {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open")
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("any")
  const [projectFilter, setProjectFilter] = useState<string>("")

  const listInput: {
    status: StatusFilter
    project?: string
    source?: Exclude<SourceFilter, "any">
    limit: number
  } = {
    status: statusFilter,
    limit: 100,
  }
  if (projectFilter) listInput.project = projectFilter
  if (sourceFilter !== "any") listInput.source = sourceFilter

  const {
    data: incidents,
    isLoading,
    refetch,
    dataUpdatedAt,
  } = trpc.incidents.list.useQuery(listInput, {
    refetchInterval: 30_000,
  })
  const { data: noiseStats } = trpc.incidents.noiseStats.useQuery(undefined, {
    refetchInterval: 60_000,
  })
  const { data: countBySource } = trpc.incidents.countBySource.useQuery(undefined, {
    refetchInterval: 60_000,
  })

  const totalRows = incidents?.length ?? 0

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Incidents</h1>
          <p className="mt-1 text-sm text-gray-500">
            Unified incident hub. Channel + paging + timeline for every signal.{" "}
            <a href="/brain/aix-command-center/architecture/incidents.md" className="text-blue-600 hover:underline">
              docs
            </a>
          </p>
        </div>
        <Button onClick={() => void refetch()} variant="outline" size="sm">
          <RefreshCw className="mr-1 h-3 w-3" />
          Refresh
        </Button>
      </div>

      {/* Watchdog stats */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-lg border bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500">Last 24h</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900">{noiseStats?.count ?? "—"}</span>
            <span className="text-xs text-gray-500">opened (threshold {noiseStats?.threshold ?? "—"})</span>
          </div>
          <div className="mt-1">
            {noiseStats?.noisy ? (
              <span className="text-xs font-medium text-red-600">⚠️ noisy — review sources</span>
            ) : (
              <span className="text-xs font-medium text-green-600">✅ healthy</span>
            )}
          </div>
        </div>
        <div className="rounded-lg border bg-white p-4 md:col-span-2">
          <div className="mb-2 text-xs uppercase tracking-wide text-gray-500">By source (last 7d)</div>
          {countBySource && countBySource.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {countBySource.map((row) => (
                <div key={row.source} className="rounded bg-gray-50 px-2 py-1 text-xs">
                  <span className="font-medium text-gray-700">{row.source}</span>
                  <span className="ml-1 text-gray-500">({row.count})</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-gray-400">No incidents in window</div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border bg-white p-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="rounded border-gray-300 text-sm"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value as SourceFilter)}
          className="rounded border-gray-300 text-sm"
        >
          {SOURCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Project (e.g. pebble)"
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
          className="rounded border-gray-300 text-sm"
        />
        <span className="ml-auto text-xs text-gray-500">
          {totalRows} {totalRows === 1 ? "result" : "results"} · refreshed{" "}
          {dataUpdatedAt ? formatRelativeTime(new Date(dataUpdatedAt)) : "—"}
        </span>
      </div>

      {/* List */}
      <div className="overflow-hidden rounded-lg border bg-white">
        {isLoading ? (
          <div className="px-6 py-8 text-center text-sm text-gray-500">Loading…</div>
        ) : !incidents || incidents.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <CheckCircle2 className="mx-auto h-8 w-8 text-green-500" />
            <p className="mt-2 text-sm font-medium text-gray-900">No incidents matching filters</p>
            <p className="text-xs text-gray-500">Try widening the status filter to see resolved.</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="border-b bg-gray-50">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Title</th>
                <th className="px-4 py-2 font-medium">Project</th>
                <th className="px-4 py-2 font-medium">Source</th>
                <th className="px-4 py-2 font-medium">Opened</th>
                <th className="px-4 py-2 font-medium">Duration</th>
                <th className="px-4 py-2 font-medium">Slack</th>
              </tr>
            </thead>
            <tbody>
              {incidents.map((inc) => (
                <tr
                  key={inc.id}
                  className="cursor-pointer border-b last:border-0 hover:bg-gray-50"
                  onClick={() =>
                    void navigate({ to: "/devtools/incidents/$incidentId", params: { incidentId: inc.id } })
                  }
                >
                  <td className="px-4 py-3">
                    <StatusBadge status={inc.status} resolvedAt={inc.resolvedAt} />
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{inc.title}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{inc.project}</td>
                  <td className="px-4 py-3">
                    <SourceBadge source={inc.source} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    <Clock className="mr-1 inline h-3 w-3" />
                    {formatRelativeTime(inc.openedAt)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {inc.resolvedAt
                      ? formatDuration(inc.durationSeconds)
                      : formatDuration(Math.floor((Date.now() - new Date(inc.openedAt).getTime()) / 1000))}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {inc.slackChannelName ? (
                      <a
                        href={`slack://channel?team=&id=${inc.slackChannelId}`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                      >
                        #{inc.slackChannelName}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
